// @ts-check
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import styled from 'styled-components';
import { get, resolveByCountry } from '@codex/helpers';
import FooterNavigation from './FooterNavigation';
import FooterCopyright from './FooterCopyright';
import IrelandMap from './IrelandMap';
import UKMap from './UKMap';

const Wrapper = styled.div`
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.shades[3]};
  max-width: 1240px;
  font-size: 12px;
  background: ${({ theme }) => theme.colors.shades[6]};

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    background: ${({ theme }) => theme.colors.shades[7]};
  }
`;

const Footer = (props) => {
  const [navigation, setNavigation] = useState(props.initialNavigation);
  const [copyright, setCopyright] = useState(props.initialCopyright);

  useEffect(() => {
    if (props.useOnlyInitialData) {
      return;
    }

    get(props.navigationApi, undefined, { credentials: 'omit' })
      .then((data) => {
        setNavigation(data.footer.nav[0]);
        setCopyright(data.footer.nav[1]);
      });
  }, [props.navigationApi, props.useOnlyInitialData]);

  return (
    <Wrapper data-qa={props.qa}>
      {navigation && (
        <LazyHydrate whenVisible>
          <FooterNavigation
            data={navigation}
            StoreFinderMap={props.StoreFinderMap}
            storeFinderTitle={props.storeFinderTitle}
            storeFinderDescription={props.storeFinderDescription}
            storeFinderLinkText={props.storeFinderLinkText}
            storeFinderLinkIcon={props.storeFinderLinkIcon}
            storeFinderUrl={props.storeFinderUrl}
          />
        </LazyHydrate>
      )}
      {copyright && (
        <LazyHydrate whenVisible>
          <FooterCopyright data={copyright.link} />
        </LazyHydrate>
      )}
    </Wrapper>
  );
};

Footer.propTypes = {
  initialNavigation: PropTypes.shape({
    footer: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
      ]))
    })
  }),
  initialCopyright: PropTypes.shape({
    footer: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
      ]))
    })
  }),
  useOnlyInitialData: PropTypes.bool,
  /**
   * URL to load the navigation from. Include query parameter `includeFooter` to load footer.
   */
  navigationApi: PropTypes.string,
  /**
   * Title of store finder section of footer
   */
  storeFinderTitle: PropTypes.string,
  /**
   * Description displayed in store finder section of footer.
   * Defaults to current country specific description.
   */
  storeFinderDescription: PropTypes.string,
  /**
   * Link to store finder application.
   * Defaults to current country production store finder URL.
   */
  storeFinderUrl: PropTypes.string,
  /**
   * Text displayed in the "Find stores" button
   */
  storeFinderLinkText: PropTypes.string,
  /**
   * Icon displayed in the "Find stores" button
   */
  storeFinderLinkIcon: PropTypes.string,
  /**
   * Map component. Defaults to current country map.
   */
  StoreFinderMap: PropTypes.func,
  qa: PropTypes.string
};

Footer.defaultProps = {
  initialNavigation: undefined,
  initialCopyright: undefined,
  useOnlyInitialData: false,
  storeFinderTitle: 'Store finder',
  storeFinderDescription: resolveByCountry(
    'To show you Currys PC World stores local to you, all we need is your postcode.',
    'To show you Currys PC World stores local to you, all we need is your eircode.'
  ),
  storeFinderUrl: resolveByCountry(
    'https://www.currys.co.uk/gbuk/store-finder',
    'https://www.currys.ie/ieen/store-finder'
  ),
  storeFinderLinkText: 'Find stores',
  storeFinderLinkIcon: 'LocationPin',
  StoreFinderMap: resolveByCountry(UKMap, IrelandMap),
  navigationApi: undefined,
  qa: undefined
};

export default Footer;
